import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Target, CheckCircle2, FileText, TrendingUp, Award, ArrowRight,
  Clock, Brain, Lightbulb, Star
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * LabDashboard - Reusable progress tracking dashboard for ProLab
 * Shows missions completed, readiness score, outputs, and recommendations
 * Designed to support future user authentication systems
 */
export default function LabDashboard({ 
  labId,
  labName,
  missions = [],
  completedMissions = [],
  outputs = [],
  readinessScore = 0,
  recommendedLabs = [],
  showFullDashboard = true
}) {
  const totalMissions = missions.length;
  const completedCount = completedMissions.length;
  const progressPercent = totalMissions > 0 ? (completedCount / totalMissions) * 100 : 0;
  
  // Calculate skill indicators based on progress
  const skillLevel = progressPercent >= 80 ? "Advanced" : progressPercent >= 50 ? "Intermediate" : progressPercent >= 20 ? "Developing" : "Beginner";
  const skillColor = progressPercent >= 80 ? "#1E3A8A" : progressPercent >= 50 ? "#2563EB" : progressPercent >= 20 ? "#3B82F6" : "#60A5FA";

  return (
    <div className="space-y-6" data-testid={`lab-dashboard-${labId}`}>
      {/* Progress Overview Card */}
      <Card className="border-[#E5E7EB] bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="font-heading text-lg" style={{ color: '#991B1B' }}>
              Lab Progress
            </span>
            <Badge 
              variant="outline"
              className="border-[#1E3A8A]/20"
              style={{ backgroundColor: `${skillColor}15`, color: skillColor }}
            >
              {skillLevel}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#1E3A8A]">Overall Progress</span>
                <span className="font-mono font-semibold text-[#1E3A8A]">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              <StatCard 
                icon={Target} 
                label="Missions" 
                value={`${completedCount}/${totalMissions}`}
                color="#1E3A8A"
              />
              <StatCard 
                icon={TrendingUp} 
                label="Readiness" 
                value={`${readinessScore}%`}
                color="#2563EB"
              />
              <StatCard 
                icon={FileText} 
                label="Outputs" 
                value={outputs.length}
                color="#3B82F6"
              />
              <StatCard 
                icon={Clock} 
                label="Status" 
                value={progressPercent >= 100 ? "Complete" : "In Progress"}
                color={progressPercent >= 100 ? "#1E3A8A" : "#60A5FA"}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {showFullDashboard && (
        <>
          {/* Missions Progress */}
          <Card className="border-[#E5E7EB] bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>
                Mission Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {missions.slice(0, 5).map((mission, index) => (
                  <MissionProgressItem 
                    key={mission.id || index}
                    mission={mission}
                    isCompleted={completedMissions.includes(mission.id)}
                    index={index}
                  />
                ))}
                {missions.length > 5 && (
                  <p className="text-sm text-[#1E3A8A]/60 text-center pt-2">
                    +{missions.length - 5} more missions
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Portfolio Outputs */}
          {outputs.length > 0 && (
            <Card className="border-[#E5E7EB] bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>
                  Portfolio Outputs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {outputs.map((output, index) => (
                    <OutputCard key={index} output={output} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Labs */}
          {recommendedLabs.length > 0 && (
            <Card className="border-[#E5E7EB] bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>
                  Recommended Next Labs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recommendedLabs.map((lab, index) => (
                    <RecommendedLabCard key={index} lab={lab} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// Reusable stat card
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="text-center p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
      <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
      <p className="font-mono text-lg font-bold" style={{ color }}>{value}</p>
      <p className="text-xs text-[#1E3A8A]/60">{label}</p>
    </div>
  );
}

// Mission progress item
function MissionProgressItem({ mission, isCompleted, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isCompleted 
          ? 'bg-[#1E3A8A]/5 border-[#1E3A8A]/20' 
          : 'bg-white border-[#E5E7EB]'
      }`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isCompleted ? 'bg-[#1E3A8A]' : 'bg-[#E5E7EB]'
      }`}>
        {isCompleted ? (
          <CheckCircle2 className="w-4 h-4 text-white" />
        ) : (
          <span className="text-sm font-mono text-[#1E3A8A]/60">{index + 1}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isCompleted ? 'text-[#1E3A8A]' : 'text-[#1E3A8A]/80'}`}>
          {mission.title}
        </p>
        <p className="text-xs text-[#1E3A8A]/50 truncate">{mission.objective}</p>
      </div>
      {isCompleted && (
        <Badge variant="outline" className="bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20 text-xs">
          Done
        </Badge>
      )}
    </motion.div>
  );
}

// Output card component
function OutputCard({ output }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
        <FileText className="w-5 h-5 text-[#1E3A8A]" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-[#1E3A8A] truncate">{output.title}</p>
        <p className="text-xs text-[#1E3A8A]/50">{output.type}</p>
      </div>
    </div>
  );
}

// Recommended lab card
function RecommendedLabCard({ lab }) {
  return (
    <Link to={lab.href || "#"}>
      <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:shadow-md transition-shadow group">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${lab.color || '#1E3A8A'}15` }}
        >
          <Lightbulb className="w-5 h-5" style={{ color: lab.color || '#1E3A8A' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1E3A8A] truncate">{lab.name}</p>
          <p className="text-xs text-[#1E3A8A]/50">{lab.reason}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-[#1E3A8A]/40 group-hover:text-[#1E3A8A] transition-colors" />
      </div>
    </Link>
  );
}

/**
 * LabDashboardCompact - Smaller version for sidebar or inline use
 */
export function LabDashboardCompact({ completedMissions = 0, totalMissions = 0, readinessScore = 0 }) {
  const progressPercent = totalMissions > 0 ? (completedMissions / totalMissions) * 100 : 0;
  
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
      <div className="flex-1">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-[#1E3A8A]">Progress</span>
          <span className="font-mono font-semibold text-[#1E3A8A]">{Math.round(progressPercent)}%</span>
        </div>
        <Progress value={progressPercent} className="h-1.5" />
      </div>
      <div className="text-center px-3 border-l border-[#E5E7EB]">
        <p className="font-mono text-lg font-bold text-[#1E3A8A]">{readinessScore}%</p>
        <p className="text-[10px] text-[#1E3A8A]/50">Readiness</p>
      </div>
    </div>
  );
}
